import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { Anchor, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token, useThemeObserver } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services';

import type { TourStepComponent } from '../../hooks/useCombinedSegments';
import { CrossIconCloseButton } from '../../common/CrossIconCloseButton';
import { useSpotlightActions } from '../../hooks/useSpotlightActions';

import ContentWrapperSpotlightGifDark from './assets/ContentWrapperSpotlightGIF-dark.gif';
import ContentWrapperSpotlightGifLight from './assets/ContentWrapperSpotlightGIF-light.gif';

export const LAST_UPDATED_SPOTLIGHT_TARGET = 'content-wrapper-last-updated-spotlight-target';
export const LAST_UPDATED_SPOTLIGHT_NAME = 'content-wrapper-last-updated-spotlight';

export const OBJECT_SIDEBAR_SPOTLIGHT_TARGET = 'content-wrapper-object-sidebar-spotlight-target';
export const OBJECT_SIDEBAR_SPOTLIGHT_NAME = 'content-wrapper-object-sidebar-spotlight';

const FLOATING_ACTION_BAR_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/get-to-know-the-new-and-simplified-content-layout/#The-floating-action-bar';
const LAST_UPDATED_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/get-to-know-the-new-and-simplified-content-layout/#Header:-Last-updated';

const linkStyles = xcss({
	color: 'color.text.inverse',
	textDecoration: 'underline',
	':hover': {
		color: 'color.text.inverse',
	},
	':visited': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
	':active': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
	':focus': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
	':visited:active': {
		color: 'color.text.inverse',
		textDecoration: 'underline',
	},
});

const floatingMenuSpotlight = defineMessages({
	heading: {
		id: 'modernize-changeboarding.floating-menu-spotlight.heading',
		defaultMessage: 'Comments, details, and more',
		description: 'Title of tutorial message describing the floating menu functionality',
	},
	body: {
		id: 'modernize-changeboarding.floating-menu-spotlight.body',
		defaultMessage: 'Work faster with a <a>floating menu</a> that gets you closer to your content.',
		description: 'Body of a tutorial message describing the floating menu functionality',
	},
});

const lastUpdatedSpotlight = defineMessages({
	heading: {
		id: 'modernize-changeboarding.last-updated-spotlight.heading',
		defaultMessage: 'Last updated is here',
		description: 'Title of tutorial message describing the last updated functionality',
	},
	body: {
		id: 'modernize-changeboarding.last-updated-spotlight.body',
		defaultMessage: 'Instantly track when your content was <a>last updated</a>.',
		description: 'Body of a tutorial message describing the last updated functionality',
	},
});

const LastUpdatedSpotlight: TourStepComponent = ({ onNext, onBack, onDismiss, progress }) => {
	const { Spotlight } = useChoreographerAssets();
	const { formatMessage } = useIntl();

	const { actions, actionsBeforeElement } = useSpotlightActions({
		progress,
		onNext,
		onBack,
		source: LAST_UPDATED_SPOTLIGHT_NAME,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<Spotlight
			actions={actions}
			actionsBeforeElement={actionsBeforeElement}
			dialogPlacement="bottom right"
			dialogWidth={300}
			heading={formatMessage(lastUpdatedSpotlight.heading)}
			headingAfterElement={
				<CrossIconCloseButton onClose={onDismiss} source={LAST_UPDATED_SPOTLIGHT_NAME} />
			}
			key={LAST_UPDATED_SPOTLIGHT_NAME}
			messageId={LAST_UPDATED_SPOTLIGHT_NAME}
			messageType="engagement"
			shouldWatchTarget
			target={LAST_UPDATED_SPOTLIGHT_TARGET}
			targetBgColor={token('elevation.surface')}
			targetRadius={4}
		>
			{formatMessage(lastUpdatedSpotlight.body, {
				a: (chunks: React.ReactNode) => (
					<Anchor
						onClick={() => {
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									source: LAST_UPDATED_SPOTLIGHT_NAME,
									action: 'clicked',
									actionSubject: 'link',
									actionSubjectId: 'exploreContentWrapper',
								},
							}).fire();
						}}
						href={LAST_UPDATED_LINK}
						target="_blank"
						testId="content-wrapper-last-updated-spotlight-link"
						xcss={linkStyles}
					>
						{chunks}
					</Anchor>
				),
			})}
		</Spotlight>
	);
};

const ObjectSidebarSpotlight: TourStepComponent = ({ onNext, onBack, onDismiss, progress }) => {
	const { Spotlight } = useChoreographerAssets();
	const { formatMessage } = useIntl();
	const theme = useThemeObserver();
	const ContentWrapperSpotlightGIF =
		theme.colorMode === 'dark' ? ContentWrapperSpotlightGifDark : ContentWrapperSpotlightGifLight;

	const { actions, actionsBeforeElement } = useSpotlightActions({
		progress,
		onNext,
		onBack,
		source: OBJECT_SIDEBAR_SPOTLIGHT_NAME,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<Spotlight
			actions={actions}
			actionsBeforeElement={actionsBeforeElement}
			dialogPlacement="left bottom"
			dialogWidth={338}
			heading={formatMessage(floatingMenuSpotlight.heading)}
			headingAfterElement={
				<CrossIconCloseButton onClose={onDismiss} source={OBJECT_SIDEBAR_SPOTLIGHT_NAME} />
			}
			key={OBJECT_SIDEBAR_SPOTLIGHT_NAME}
			image={ContentWrapperSpotlightGIF}
			messageId={OBJECT_SIDEBAR_SPOTLIGHT_NAME}
			messageType="engagement"
			shouldWatchTarget
			target={OBJECT_SIDEBAR_SPOTLIGHT_TARGET}
			targetBgColor={token('elevation.surface')}
			targetRadius={12}
		>
			{formatMessage(floatingMenuSpotlight.body, {
				a: (chunks: React.ReactNode) => (
					<Anchor
						onClick={() => {
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									source: OBJECT_SIDEBAR_SPOTLIGHT_NAME,
									action: 'clicked',
									actionSubject: 'link',
									actionSubjectId: 'exploreContentWrapper',
								},
							}).fire();
						}}
						href={FLOATING_ACTION_BAR_LINK}
						target="_blank"
						testId="content-wrapper-object-sidebar-spotlight-link"
						xcss={linkStyles}
					>
						{chunks}
					</Anchor>
				),
			})}
		</Spotlight>
	);
};

export const Spotlights = {
	LastUpdatedSpotlight,
	ObjectSidebarSpotlight,
};
